import { useEditorStore } from "@/stores/editor/editor-store";
import { ProjectsApi } from "@/services/api/projects";
import { buildPersistedProjectState } from "@/services/persistence/persistedProjectState";
import { logger } from "@/utils";

/**
 * Check if the user is currently in the editor with unsaved work.
 * Reads imperatively from Zustand (no React context needed).
 */
export function isInEditorWithUnsavedWork(): boolean {
  try {
    const { isModified, components } = useEditorStore.getState();
    // Only consider it unsaved if there are actual components and modifications
    return isModified && components.length > 0;
  } catch {
    return false;
  }
}

/**
 * Attempt to auto-save the current project before navigating away.
 * Resolves even if save fails (best-effort).
 */
export async function saveBeforeNavigate(): Promise<void> {
  if (!isInEditorWithUnsavedWork()) return;

  try {
    const state = useEditorStore.getState();
    const { currentPageId } = state;

    // Extract projectId from the current URL
    const match = window.location.pathname.match(/\/editor\/([a-f0-9]+)/i);
    const projectId = match?.[1];
    if (!projectId) return;

    const allPages = state.getAllPagesWithComponents();
    if (allPages.length === 0) return;

    await ProjectsApi.autoSaveProject(
      projectId,
      buildPersistedProjectState({
        pagesWithComponents: allPages,
        currentPageId,
        schemaVersion: state.project?.schemaVersion,
        lastModified: new Date(),
        metadata: {
          currentPageId: currentPageId || null,
        },
      }),
    );

    logger.info("Save-before-navigate completed", { projectId });
  } catch (error) {
    // Best-effort — don't block navigation on save failure
    logger.warn("Save-before-navigate failed (best-effort):", error);
  }
}

/**
 * Navigate safely: saves editor work first, then navigates.
 * Accepts either a React Router navigate function or falls back to window.location.
 */
export async function safeNavigate(
  path: string,
  navigate?: (path: string, opts?: { replace?: boolean }) => void,
  opts?: { replace?: boolean },
): Promise<void> {
  await saveBeforeNavigate();

  if (navigate) {
    navigate(path, opts);
  } else {
    if (opts?.replace) {
      window.location.replace(path);
    } else {
      window.location.href = path;
    }
  }
}
